import { useLayoutEffect, useRef, useState } from "react";
import { Check, Download, Printer, Image as ImageIcon, GraduationCap, ShieldCheck, Accessibility, TrendingUp, BarChart3, BadgeCheck, Mail, Globe } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { printTools } from "@/lib/scroll";
import { trackDownload } from "@/lib/api";
import { ORG } from "@/data/tools";
import { SUPPORTING, INCLUDED, SUPPORTS, ALIGNED, ACCESS, PLANNING, IMPACT, STATUS_V1 } from "@/data/poster";

const NAVY = "#1F3A5F";
const TEAL = "#2F8F9D";
const GOLD = "#D9A441";
const SHEET_W = 794;

const useFit = () => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, e.contentRect.width / SHEET_W)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, scale];
};

const Box = ({ title, Icon, items, tick, className = "" }) => (
  <div className={`rounded-[3mm] border-[0.5mm] p-[3mm] ${className}`} style={{ borderColor: TEAL }}>
    <h2 className="mb-[1.8mm] flex items-center gap-[1.5mm] border-b-[0.6mm] pb-[1.2mm] text-[9.5pt] font-extrabold uppercase tracking-[0.04em]" style={{ color: NAVY, borderColor: GOLD }}>
      <Icon className="h-[4.2mm] w-[4.2mm] shrink-0" style={{ color: TEAL }} aria-hidden /> {title}
    </h2>
    <ul className="space-y-[0.9mm]">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-[1.4mm] text-[7.6pt] font-semibold leading-[1.25]" style={{ color: NAVY }}>
          {tick ? <Check className="mt-[0.2mm] h-[3mm] w-[3mm] shrink-0" style={{ color: TEAL }} strokeWidth={3} aria-hidden /> : <span className="mt-[1.3mm] h-[1.3mm] w-[1.3mm] shrink-0 rounded-full" style={{ background: GOLD }} aria-hidden />}
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Header = () => (
  <header className="rounded-[3mm] px-[6mm] py-[5mm] text-white" style={{ background: NAVY }}>
    <div className="flex items-start justify-between gap-[4mm]">
      <p className="text-[10pt] font-extrabold tracking-[0.12em]" style={{ color: GOLD }}>THE SEND ACCOUNTABILITY PROJECT</p>
      <span className="rounded-full px-[3mm] py-[0.8mm] text-[8pt] font-extrabold" style={{ background: GOLD, color: NAVY }}>Version 1.0</span>
    </div>
    <h1 className="mt-[2mm] text-[23pt] font-extrabold leading-[1.05] tracking-[-0.01em]">SENDCO QUICK START &amp; IMPROVEMENT WORKBOOK</h1>
    <p className="mt-[2mm] text-[10pt] font-semibold opacity-95">Practical SEND Reflection, Review and Improvement Planning for Schools</p>
    <div className="mt-[3mm] flex flex-wrap items-center gap-[1.6mm]">
      <span className="text-[8pt] font-bold">Supporting:</span>
      {SUPPORTING.map((s) => (
        <span key={s} className="flex items-center gap-[1mm] rounded-full bg-white px-[2.4mm] py-[0.6mm] text-[7.6pt] font-bold" style={{ color: NAVY }}>
          <Check className="h-[2.8mm] w-[2.8mm]" style={{ color: TEAL }} strokeWidth={3} aria-hidden />{s}
        </span>
      ))}
    </div>
  </header>
);

const Hero = () => (
  <div className="rounded-[3mm] px-[5mm] py-[3.4mm] text-center text-white" style={{ background: TEAL }}>
    <p className="text-[13pt] font-extrabold tracking-[0.06em]">FREE DOWNLOAD <span style={{ color: GOLD }}>•</span> NO LOGIN REQUIRED <span style={{ color: GOLD }}>•</span> NO REGISTRATION REQUIRED</p>
    <p className="mt-[1mm] text-[9pt] font-bold">Download • Complete • Save • Print • Revisit</p>
  </div>
);

const Included = () => (
  <div className="rounded-[3mm] p-[3.4mm]" style={{ background: "#EEF6F7" }}>
    <h2 className="mb-[2.4mm] text-[10.5pt] font-extrabold uppercase tracking-[0.04em]" style={{ color: NAVY }}>What's included</h2>
    <ul className="grid grid-cols-4 gap-[2mm]">
      {INCLUDED.map(([t, Icon]) => (
        <li key={t} className="flex items-center gap-[1.8mm] rounded-[2mm] bg-white p-[1.8mm]">
          <span className="flex h-[7mm] w-[7mm] shrink-0 items-center justify-center rounded-full" style={{ background: NAVY }}>
            <Icon className="h-[3.8mm] w-[3.8mm]" style={{ color: GOLD }} aria-hidden />
          </span>
          <span className="text-[7.4pt] font-bold leading-[1.2]" style={{ color: NAVY }}>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const SheetFooter = () => (
  <footer className="rounded-[3mm] px-[6mm] py-[3.6mm] text-white" style={{ background: NAVY }}>
    <div className="flex items-start justify-between gap-[5mm]">
      <div>
        <p className="text-[10pt] font-extrabold tracking-[0.08em]">THE SEND ACCOUNTABILITY PROJECT LTD</p>
        <p className="mt-[1mm] max-w-[105mm] text-[7.4pt] leading-[1.3] opacity-95">Supporting schools, families and professionals through practical SEND resources, toolkits and insights.</p>
        <p className="mt-[1.4mm] text-[7.4pt] font-bold" style={{ color: GOLD }}>SEND Family Hub</p>
      </div>
      <div className="space-y-[0.6mm] text-right text-[7.2pt]">
        <p className="flex items-center justify-end gap-[1mm]"><Globe className="h-[3mm] w-[3mm]" aria-hidden />thesendaccountabilityproject.online</p>
        <p className="flex items-center justify-end gap-[1mm]"><Mail className="h-[3mm] w-[3mm]" aria-hidden /><a href={`mailto:${ORG.director}`}>{ORG.director}</a></p>
        <p className="flex items-center justify-end gap-[1mm]"><Mail className="h-[3mm] w-[3mm]" aria-hidden /><a href={`mailto:${ORG.datalead}`}>{ORG.datalead}</a></p>
        <p>ICO Registration: ZC252093 · Company No. 17420678</p>
      </div>
    </div>
    <p className="mt-[2.4mm] border-t pt-[2mm] text-center text-[9pt] font-extrabold tracking-[0.06em]" style={{ borderColor: GOLD, color: GOLD }}>Practical Tools • Clear Information • Better Outcomes</p>
  </footer>
);

const Sheet = () => (
  <article className="poster-sheet flex h-[296mm] w-[210mm] flex-col gap-[3mm] overflow-hidden bg-white p-[8mm] font-sans" style={{ fontFamily: "'Atkinson Hyperlegible', Arial, sans-serif" }} aria-label="SENDCO Quick Start and Improvement Workbook poster" data-testid="poster-sheet">
    <Header />
    <Hero />
    <Included />
    <div className="grid flex-1 grid-cols-3 gap-[3mm]">
      <Box title="Supports schools to" Icon={GraduationCap} items={SUPPORTS} />
      <Box title="Aligned to" Icon={ShieldCheck} items={ALIGNED} />
      <Box title="Accessibility" Icon={Accessibility} items={ACCESS} tick />
      <Box title="Improvement planning" Icon={TrendingUp} items={PLANNING} />
      <Box title="Impact tracking" Icon={BarChart3} items={IMPACT} />
      <Box title="Version 1.0 status" Icon={BadgeCheck} items={STATUS_V1} tick className="bg-[#FBF5E8]" />
    </div>
    <SheetFooter />
  </article>
);

export default function PosterPage() {
  const [ref, scale] = useFit();
  return (
    <>
      <TopBar minimal />
      <main id="main" tabIndex={-1} className="outline-none">
        <section className="no-print mx-auto max-w-[1100px] px-5 pb-8 pt-[150px] sm:px-8 lg:pt-[178px]" aria-labelledby="poster-title">
          <p className="eyebrow mb-4">Share with your Headteacher and governors</p>
          <h1 id="poster-title" className="font-display text-[2.4rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">Quick Start <span className="italic text-brand">poster</span></h1>
          <p className="mt-5 max-w-2xl text-lg text-ink-soft">An A4 poster for staffrooms, governor meetings and conferences. Download the print-quality PDF, or the image for email and social media.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="/pdfs/quick-start-poster.pdf" download="quick-start-poster.pdf" onClick={() => trackDownload("poster-pdf")} className="btn btn-brand text-base" data-testid="poster-download-pdf"><Download size={18} aria-hidden /> Download A4 poster (PDF)</a>
            <button type="button" onClick={() => printTools("poster")} className="btn btn-ghost text-base" data-testid="poster-print"><Printer size={18} aria-hidden /> Print poster</button>
            <a href="/send-quick-start-poster.png" download onClick={() => trackDownload("poster-png")} className="btn btn-ghost text-base" data-testid="poster-download-png"><ImageIcon size={18} aria-hidden /> Download social image (PNG)</a>
          </div>
          <p className="mt-3 text-sm text-ink-soft">If you print from this page, choose paper size A4 and turn on "Background graphics".</p>
        </section>
        <div className="print-scope mx-auto max-w-[1100px] px-5 pb-20 sm:px-8">
          <div id="poster" className="tool-section">
            <div ref={ref} className="poster-frame w-full" style={{ height: 1119 * scale }}>
              <div className="poster-scale origin-top-left shadow-[0_20px_60px_-20px_rgba(31,58,95,0.35)]" style={{ transform: `scale(${scale})`, width: SHEET_W }}>
                <Sheet />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
